<script setup lang="ts">
definePageMeta({
  custom_meta_data: {
    topic: 'lainnya',
    message:
      'Halo, saya tertarik dengan fitur Return Management System (RMS) dari KiriminAja'
  }
})

useDefaultMeta({
  title: 'Return Management System – Retur Terkendali, Bisnis Aman',
  description:
    'Kelola retur otomatis, cepat, dan transparan dengan RMS KiriminAja. Turunkan angka retur hingga 30% dan tingkatkan kepuasan pelanggan bisnismu.',
  robots: 'index, follow',
  schema: {
    title: 'Return Management System'
  }
})

const { utmUrlWithCustomMetaData } = useUTM()

const contactLink = utmUrlWithCustomMetaData('/kontak-kami')

const faqs = [
  {
    question:
      'Apakah Return Management System (RMS) hanya bisa diakses di dashboard KiriminAja?',
    answer:
      'Tidak. RMS dapat diakses melalui akun KiriminAja Anda (Aplikasi & Dashboard), Plugin WooCommerce, Plugin Shopify, atau API KiriminAja.'
  },
  {
    question: 'Apakah mendukung banyak ekspedisi?',
    answer: 'Ya, RMS mendukung seluruh ekspedisi dalam jaringan KiriminAja.'
  },
  {
    question: 'Bagaimana sistem mendeteksi penyebab retur?',
    answer:
      'Melalui data tracking, validasi alamat, dan feedback pelanggan secara otomatis.'
  },
  {
    question: 'Apakah cocok untuk perusahaan dengan ribuan pengiriman?',
    answer:
      'Sangat cocok. RMS didesain untuk enterprise-scale dengan multi-user dashboard.'
  },
  {
    question: 'Apakah tersedia laporan analitik retur?',
    answer:
      'Ya. Laporan performa retur dan SLA bisa diunduh kapan saja melalui dashboard.'
  }
]
</script>

<template>
  <OtherRmsHero :button-link="contactLink" />

  <UiSectionWrapper
    image-class="order-first lg:order-first"
    title="Retur Bukan Sekadar Masalah, Tapi Indikator Kinerja Operasional"
    description="1 dari 10 pengiriman di Indonesia berisiko retur. Tanpa sistem yang tepat, retur bisa jadi sumber kerugian bisnis."
    hero-image="/images/solusi/rms/banner-1.webp"
    :with-action="false"
    :items="[
      {
        title: 'Biaya Logistik Membengkak',
        icon: 'material-symbols:payments-outline',
        description:
          'Proses retur manual menambah biaya operasional dan memperbesar potensi kehilangan barang.'
      },
      {
        title: 'Waktu Operasional Terbuang',
        icon: 'material-symbols:history-toggle-off',
        description:
          'Tim Anda harus melacak satu per satu status retur dari berbagai ekspedisi — memperlambat alur kerja.'
      },
      {
        title: 'Kepercayaan Pelanggan Menurun',
        icon: 'material-symbols:thumb-down-outline',
        description:
          'Retur tanpa transparansi membuat pelanggan kecewa dan menurunkan tingkat repeat order.'
      }
    ]"
  />
  <UiSectionWrapper
    title="Solusi Otomatis untuk Semua Proses Retur"
    description="RMS KiriminAja adalah sistem yang mengelola seluruh siklus retur dari paket gagal kirim, penjadwalan ulang, hingga laporan penyebab retur — semua dalam satu dashboard."
    hero-image="/images/solusi/rms/banner-2.webp"
    :items="[
      {
        title: 'Retur Otomatis',
        icon: 'material-symbols:undo',
        description: 'Proses retur berjalan tanpa intervensi manual.'
      },
      {
        title: 'Analitik Real-Time',
        icon: 'material-symbols:timelapse-outline',
        description: 'Lihat performa retur dan SLA kapan pun.'
      },
      {
        title: 'Tracking Terpadu',
        icon: 'material-symbols:my-location-outline',
        description: 'Pantau seluruh ekspedisi dari satu dashboard.'
      },
      {
        title: 'Terintegrasi Penuh',
        icon: 'material-symbols:integration-instructions-outline',
        description: 'Sinkron dengan sistem pengiriman KiriminAja.'
      }
    ]"
  >
    <template #action>
      <div class="flex">
        <UiButton
          label="Konsultasi dengan Tim Kami"
          :to="contactLink"
          class="inline-flex"
        />
      </div>
    </template>
  </UiSectionWrapper>
  <OtherRmsBenefitMore />
  <OtherRmsBenefit />
  <OtherRmsSolutions />
  <UiSectionWrapper
    image-class="order-first lg:order-first"
    title="Terhubung Langsung dengan Control Tower KiriminAja"
    description="RMS bekerja dalam satu ekosistem dengan sistem utama KiriminAja"
    hero-image="/images/roms-banner-4.webp"
    :items="[
      {
        title: 'Kemudahan Klaim Retur',
        icon: 'material-symbols:task-alt',
        description: 'Sinkronisasi dari pengiriman ke retur.'
      },
      {
        title: 'Control Tower Monitoring',
        icon: 'material-symbols:cell-tower',
        description: 'Pantau SLA dan performa pengiriman secara menyeluruh.'
      },
      {
        title: 'Reporting Dashboard',
        icon: 'material-symbols:monitor-outline',
        description:
          'Dapatkan laporan retur dan insight performa lintas ekspedisi.'
      }
    ]"
  >
    <template #action>
      <div class="flex">
        <UiButton
          label="Konsultasi dengan Tim Kami"
          :to="contactLink"
          class="inline-flex"
        />
      </div>
    </template>
  </UiSectionWrapper>

  <FaqBase
    title="Pertanyaan Seputar Return Management System (RMS)"
    :faqs="faqs"
  />
  <CtaSection
    title="Saatnya Ubah Retur Jadi Sistem yang Terkendali"
    subtitle="Return Management System"
    description="Lihat bagaimana RMS KiriminAja membantu bisnis besar menghemat waktu, biaya, dan menjaga reputasi merek Anda."
  >
    <template #action>
      <div class="flex">
        <UiButton
          label="Konsultasi dengan Tim Kami"
          :to="contactLink"
          class="inline-flex"
        />
      </div>
    </template>
    <template #hero>
      <NuxtImg
        alt="Aplikasi Kirim Paket"
        loading="lazy"
        class="w-full max-w-160"
        src="/images/solusi/rms/cta.webp"
      />
    </template>
  </CtaSection>
</template>
